import { Fragment } from "react"

interface Props {
  text: string
  className?: string
}

interface Block {
  lead?: string
  section?: string
  bullets?: string[]
  paragraph?: string
}

const SECTION_LABEL_MAX_WORDS = 5

function isSectionLabel(s: string): boolean {
  if (!s) return false
  const words = s.split(/\s+/)
  return words.length >= 1 && words.length <= SECTION_LABEL_MAX_WORDS && /^[A-Z]/.test(s) && !/[.!?]/.test(s)
}

function extractTrailingSectionLabel(lead: string): { body: string | null; label: string | null } {
  if (!lead) return { body: null, label: null }
  const idx = lead.lastIndexOf(". ")
  if (idx !== -1) {
    const body = lead.slice(0, idx + 1).trim()
    const tail = lead.slice(idx + 2).trim()
    if (isSectionLabel(tail)) return { body, label: tail }
  } else if (isSectionLabel(lead.trim())) {
    return { body: null, label: lead.trim() }
  }
  return { body: lead, label: null }
}

function parseDescription(text: string): Block[] {
  const paragraphs = text.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean)
  const blocks: Block[] = []

  for (const para of paragraphs) {
    if (para.includes("•")) {
      const chunks = para.split(/\s*•\s*/).map((s) => s.trim()).filter(Boolean)
      const lead = para.startsWith("•") ? null : chunks.shift() ?? null
      const { body, label } = lead ? extractTrailingSectionLabel(lead) : { body: null, label: null }
      blocks.push({
        lead: body ?? undefined,
        section: label ?? undefined,
        bullets: chunks,
      })
    } else {
      blocks.push({ paragraph: para })
    }
  }
  return blocks
}

export function FormattedDescription({ text, className }: Props) {
  const blocks = parseDescription(text)

  return (
    <div className={className ?? "space-y-3 text-sm"}>
      {blocks.map((b, i) => (
        <Fragment key={i}>
          {b.paragraph && <p className="whitespace-pre-line">{b.paragraph}</p>}
          {b.lead && <p className="whitespace-pre-line">{b.lead}</p>}
          {b.section && (
            <h4 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              {b.section}
            </h4>
          )}
          {b.bullets && b.bullets.length > 0 && (
            <ul className="ml-5 list-disc space-y-1 sm:grid sm:grid-cols-2 sm:gap-x-6">
              {b.bullets.map((item, j) => (
                <li key={j}>{item}</li>
              ))}
            </ul>
          )}
        </Fragment>
      ))}
    </div>
  )
}
